import React from 'react';
import CreateScheduleModal from './CreateScheduleModal';
import { Schedule } from '../../types';
import { ScheduleFormData } from '../../hooks/useSchedules';

/**
 * Props for the EditScheduleModal component
 * 
 * Unlike CreateScheduleModal, the initialData prop is required here
 * since we're editing an existing schedule.
 */
export interface EditScheduleModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Callback when the modal is dismissed */
  onDismiss: () => void;
  /** Callback when the form is submitted with updated schedule data */
  onSubmit: (schedule: ScheduleFormData) => Promise<void>;
  /** The existing schedule data to edit - REQUIRED for edit mode */
  schedule: Schedule;
  /** The job ID this schedule belongs to */
  jobId: string;
}

/**
 * EditScheduleModal Component
 * 
 * A wrapper around CreateScheduleModal that enforces edit mode by requiring
 * the schedule data to be provided. This component:
 * - Pre-populates all form fields with the existing schedule's values
 * - Handles the update API call through the onSubmit callback
 * - Displays "Edit Schedule" as the modal title
 * - Shows "Save Changes" as the submit button text
 * 
 * The underlying CreateScheduleModal handles all the form logic, validation,
 * and UI rendering. This wrapper simply ensures type safety by requiring
 * the schedule prop.
 * 
 * Requirements: 3.1, 3.2, 3.4, 3.5
 * - 3.1: Opens modal pre-populated with schedule's current values
 * - 3.2: Allows modification of Name, Description, Cron Expression, Start Date, End Date
 * - 3.4: Calls UpdateSchedule API on submit
 * - 3.5: Displays error message and keeps modal open on API error
 */
const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  visible,
  onDismiss,
  onSubmit,
  schedule,
  jobId,
}) => {
  return (
    <CreateScheduleModal
      visible={visible}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
      initialData={schedule}
      jobId={jobId}
    />
  );
};

export default EditScheduleModal;
